import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useOrgStore } from '@/stores/orgStore'
import { usePublishPageData } from '@/stores/pageDataStore'
import { analyticsApi } from '@/api/clients/analyticsApi'
import { accountApi } from '@/api/clients/accountApi'
import type { FrameworkPosture, TrendPoint, MoneyPoint } from '@/api/types'
import { PageHeader, Card, Button, Stat } from '@/components/common/ui'
import { Spinner, ErrorState } from '@/components/common/State'
import { BandBadge } from '@/components/common/Badge'
import { MoneyValue } from '@/components/common/MoneyValue'
import { DistributionDonut } from '@/components/charts/DistributionDonut'
import { QuantumRiskDonut } from '@/components/charts/QuantumRiskDonut'
import { TrendLine } from '@/components/charts/TrendLine'
import { MoneyTrendLine } from '@/components/charts/MoneyTrendLine'

// Intl uses the browser's resolved timezone (the user's local zone) by default;
// timeZoneName makes that explicit in the rendered output.
const LOCAL_DT_OPTS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZoneName: 'short',
}

export default function Reports() {
  const { currentOrgId: orgId, orgName: orgNameFallback } = useOrgStore()

  const org = useQuery({
    queryKey: ['org', orgId],
    queryFn: () => accountApi.getOrg(orgId),
    enabled: !!orgId,
    staleTime: 5 * 60_000,
  })
  const orgName = org.data?.name || orgNameFallback || orgId

  const report = useQuery({
    queryKey: ['exec-report', orgId],
    queryFn: () => analyticsApi.executiveReport(orgId),
    enabled: !!orgId,
  })
  const summary = useQuery({
    queryKey: ['summary', orgId],
    queryFn: () => analyticsApi.summary(orgId),
    enabled: !!orgId,
  })
  const crosswalk = useQuery({
    queryKey: ['crosswalk', orgId],
    queryFn: () => analyticsApi.crosswalk(orgId),
    enabled: !!orgId,
  })
  const trends = useQuery({
    queryKey: ['trends', orgId],
    queryFn: () => analyticsApi.trends(orgId),
    enabled: !!orgId,
  })
  const moneyTrends = useQuery({
    queryKey: ['money-trends', orgId],
    queryFn: () => analyticsApi.moneyTrends(orgId),
    enabled: !!orgId,
    retry: false,
  })

  usePublishPageData(
    useMemo(() => {
      const sm = summary.data
      return {
        page: 'Executive Report',
        // Structured figures so the assistant has exact numbers (not just prose).
        posture_band: sm?.band,
        risk_score: sm?.risk_score,
        assets_scored: sm?.asset_count,
        band_distribution: sm?.distribution,
        quantum_risk: sm?.quantum_risk,
        overdue_count: sm?.overdue_count,
        money: sm ? { total: sm.money.total, currency: sm.money.currency, top: sm.money.top } : undefined,
        report_excerpt: typeof report.data === 'string' ? report.data.slice(0, 3000) : null,
      }
    }, [report.data, summary.data])
  )

  // Print the full report (charts included) to PDF. A scoped @media print stylesheet
  // isolates #exec-report; dark mode is temporarily forced light for legible ink and
  // restored after printing (visual only — ThemeContext state untouched).
  const printReport = () => {
    const html = document.documentElement
    const wasDark = html.classList.contains('dark')
    // Expand every collapsed <details> (e.g. "Full written report") so it prints.
    const details = Array.from(document.querySelectorAll<HTMLDetailsElement>('#exec-report details'))
    const wasOpen = details.map((d) => d.open)
    const restore = () => {
      if (wasDark) html.classList.add('dark')
      details.forEach((d, i) => (d.open = wasOpen[i]))
      window.removeEventListener('afterprint', restore)
    }
    if (wasDark) html.classList.remove('dark')
    details.forEach((d) => (d.open = true))
    window.addEventListener('afterprint', restore)
    window.print()
  }

  const s = summary.data
  const generated = new Date().toLocaleString(undefined, LOCAL_DT_OPTS)

  // Post-process the deterministic/AI narrative: show the org name instead of its id,
  // and render the server's UTC timestamp in the viewer's local timezone.
  const narrative = useMemo(() => {
    let t = report.data ?? ''
    if (orgId && orgName && orgName !== orgId) t = t.split(orgId).join(orgName)
    t = t.replace(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z/g, (m) => {
      const d = new Date(m)
      return isNaN(d.getTime()) ? m : d.toLocaleString(undefined, LOCAL_DT_OPTS)
    })
    return t
  }, [report.data, orgId, orgName])

  return (
    <div>
      <PageHeader
        title="Executive Report"
        subtitle="Board-ready quantum-risk summary — deterministic figures, optional AI narration"
        actions={
          <Button icon="download" onClick={printReport} disabled={!report.data}>
            Download PDF
          </Button>
        }
      />

      {summary.isLoading ? (
        <Card>
          <Spinner label="Generating report…" />
        </Card>
      ) : summary.error ? (
        <Card>
          <ErrorState error={summary.error} />
        </Card>
      ) : (
        <div className="max-w-4xl mx-auto">
          <div id="exec-report" className="bg-surface-light dark:bg-surface-dark rounded-2xl border border-border-light dark:border-border-dark shadow-ambient overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-primary to-secondary" />
            <div className="p-8 md:p-10 space-y-8">
              {/* Cover header */}
              <header className="border-b border-border-light dark:border-border-dark pb-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="label-sm uppercase tracking-label text-subtle-light dark:text-subtle-dark">
                      Quantizant · Quantum Risk
                    </p>
                    <h1 className="mt-1 text-2xl font-bold tracking-display text-text-light dark:text-text-dark">
                      Executive Report
                    </h1>
                  </div>
                  {s && <BandBadge band={s.band} />}
                </div>
                <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <MetaField label="Organisation" value={orgName} />
                  <MetaField label="Generated" value={generated} />
                </dl>
              </header>

              {s && (
                <>
                  {/* KPI row */}
                  <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Stat
                      label="Quantum risk score"
                      value={s.risk_score?.toFixed(0)}
                      hint={<BandBadge band={s.band} />}
                    />
                    <Stat
                      label="$-at-risk"
                      value={<MoneyValue value={s.money.total} currency={s.money.currency} />}
                      hint={`${s.money.confidence} confidence`}
                    />
                    <Stat label="Assets scored" value={s.asset_count} hint="in scope" />
                    <Stat
                      label="Critical + High"
                      value={s.distribution.critical + s.distribution.high}
                      hint="need migration"
                    />
                  </section>

                  {/* Posture + distribution */}
                  <section className="grid md:grid-cols-2 gap-6">
                    <ReportCard title="Quantum-vulnerability distribution">
                      <div className="flex h-48 items-center gap-4">
                        <div className="h-28 w-28 shrink-0">
                          <QuantumRiskDonut dist={s.quantum_risk} showLegend={false} />
                        </div>
                        <ul className="flex-1 space-y-1.5 text-xs">
                          {(
                            [
                              { key: 'vulnerable', label: 'Vulnerable', hex: '#C22221' },
                              { key: 'partial', label: 'Partial', hex: '#e8734f' },
                              { key: 'hybrid', label: 'Hybrid', hex: '#eab308' },
                              { key: 'safe', label: 'Safe', hex: '#22c55e' },
                              { key: 'unknown', label: 'Unknown', hex: '#94a3b8' },
                            ] as const
                          ).map((b) => {
                            const v = s.quantum_risk[b.key]
                            const pct = s.asset_count > 0 ? Math.round((v / s.asset_count) * 100) : 0
                            return (
                              <li key={b.key} className="flex items-center gap-2">
                                <span className="h-2 w-2 shrink-0 rounded-sm" style={{ backgroundColor: b.hex }} />
                                <span className="flex-1 text-text-light dark:text-text-dark">{b.label}</span>
                                <span className="font-semibold text-text-light dark:text-text-dark">{v.toLocaleString()}</span>
                                <span className="w-8 text-right text-subtle-light dark:text-subtle-dark">{pct}%</span>
                              </li>
                            )
                          })}
                        </ul>
                      </div>
                    </ReportCard>
                    <ReportCard title="Band distribution">
                      <div className="flex h-48 items-center gap-4">
                        <div className="h-28 w-28 shrink-0">
                          <DistributionDonut distribution={s.distribution} showLegend={false} />
                        </div>
                        <ul className="flex-1 space-y-1.5 text-xs">
                          {(
                            [
                              { key: 'critical', label: 'Critical', hex: '#C22221' },
                              { key: 'high', label: 'High', hex: '#e8734f' },
                              { key: 'medium', label: 'Medium', hex: '#eab308' },
                              { key: 'low', label: 'Low', hex: '#94C8E4' },
                              { key: 'info', label: 'Info', hex: '#94a3b8' },
                            ] as const
                          ).map((b) => {
                            const v = s.distribution[b.key]
                            const pct = s.asset_count > 0 ? Math.round((v / s.asset_count) * 100) : 0
                            return (
                              <li key={b.key} className="flex items-center gap-2">
                                <span className="h-2 w-2 shrink-0 rounded-sm" style={{ backgroundColor: b.hex }} />
                                <span className="flex-1 text-text-light dark:text-text-dark">{b.label}</span>
                                <span className="font-semibold text-text-light dark:text-text-dark">{v.toLocaleString()}</span>
                                <span className="w-8 text-right text-subtle-light dark:text-subtle-dark">{pct}%</span>
                              </li>
                            )
                          })}
                        </ul>
                      </div>
                    </ReportCard>
                  </section>

                  {/* Compliance posture + top $ */}
                  <section className="grid md:grid-cols-2 gap-6">
                    <ReportCard title="Compliance posture">
                      <FrameworkPostureBars frameworks={crosswalk.data ?? []} />
                    </ReportCard>
                    <ReportCard title="Top systems by $-at-risk">
                      <TopSystems top={s.money.top ?? []} currency={s.money.currency} />
                    </ReportCard>
                  </section>

                  {/* Trend */}
                  {(trends.data?.length ?? 0) >= 2 && (
                    <section>
                      <ReportCard title="Risk trend (critical + high)">
                        <div className="h-52">
                          <TrendLine points={trends.data as TrendPoint[]} />
                        </div>
                      </ReportCard>
                    </section>
                  )}

                  {/* $-at-Risk trend */}
                  {(moneyTrends.data?.length ?? 0) >= 2 && (
                    <section>
                      <ReportCard title="$-at-Risk trend">
                        <div className="h-52">
                          <MoneyTrendLine points={moneyTrends.data as MoneyPoint[]} currency={s.money.currency} />
                        </div>
                      </ReportCard>
                    </section>
                  )}
                </>
              )}

              {/* Written narrative (deterministic / AI-narrated) */}
              <section>
                <details className="group rounded-xl border border-border-light dark:border-border-dark">
                  <summary className="flex items-center gap-2 px-4 py-3 cursor-pointer select-none list-none text-sm font-semibold text-text-light dark:text-text-dark">
                    <span className="material-icons text-lg text-primary dark:text-secondary">
                      description
                    </span>
                    Full written report
                    <span className="ml-auto material-icons text-base text-subtle-light dark:text-subtle-dark group-open:rotate-180 transition-transform">
                      expand_more
                    </span>
                  </summary>
                  <div className="px-6 pb-6 pt-2 border-t border-border-light dark:border-border-dark">
                    {report.isLoading ? (
                      <Spinner label="Generating narrative…" />
                    ) : report.error ? (
                      <ErrorState error={report.error} />
                    ) : (
                      <article
                        className="prose prose-slate dark:prose-invert max-w-none
                          prose-headings:tracking-tight
                          prose-h1:text-xl prose-h1:font-bold prose-h1:text-primary dark:prose-h1:text-secondary
                          prose-h2:text-base prose-h2:font-semibold prose-h2:mt-6 prose-h2:mb-2 prose-h2:pb-1.5
                          prose-h2:border-b prose-h2:border-border-light dark:prose-h2:border-border-dark
                          prose-p:my-2 prose-li:my-0.5 prose-table:text-sm prose-th:text-left"
                      >
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{narrative}</ReactMarkdown>
                      </article>
                    )}
                  </div>
                </details>
              </section>

              <p className="text-[11px] text-subtle-light dark:text-subtle-dark border-t border-border-light dark:border-border-dark pt-4">
                Deterministic report (§5.7.1). Dollar figures are ground truth; never
                AI-originated (§12.1).
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// MetaField is one labelled header field (Organisation / Generated / Framing) with a
// left divider on wide screens so the fields read as distinct columns.
function MetaField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="sm:border-l sm:first:border-l-0 sm:pl-4 border-border-light dark:border-border-dark">
      <dt className="label-sm uppercase tracking-label text-subtle-light dark:text-subtle-dark">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm font-medium text-text-light dark:text-text-dark break-words">
        {value}
      </dd>
    </div>
  )
}

function ReportCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border-light dark:border-border-dark p-5">
      <p className="label-sm uppercase tracking-label text-subtle-light dark:text-subtle-dark mb-3">
        {title}
      </p>
      {children}
    </div>
  )
}

function postureColor(pct: number): string {
  if (pct >= 80) return 'bg-success'
  if (pct >= 50) return 'bg-warning'
  return 'bg-error'
}

function FrameworkPostureBars({ frameworks }: { frameworks: FrameworkPosture[] }) {
  if (frameworks.length === 0) {
    return <p className="text-sm text-subtle-light dark:text-subtle-dark">No frameworks in scope.</p>
  }
  return (
    <div className="space-y-3">
      {frameworks.map((f) => (
        <div key={f.framework}>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-text-light dark:text-text-dark font-medium">{f.framework}</span>
            <span className="text-subtle-light dark:text-subtle-dark">
              {f.posture_pct}% · {f.safe}/{f.in_scope} safe
            </span>
          </div>
          <div className="h-2 rounded-full bg-container-light dark:bg-container-dark overflow-hidden">
            <div
              className={`h-full rounded-full ${postureColor(f.posture_pct)}`}
              style={{ width: `${Math.min(100, Math.max(0, f.posture_pct))}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

function TopSystems({ top, currency }: { top: { name: string; value: number }[]; currency: string }) {
  if (top.length === 0) {
    return <p className="text-sm text-subtle-light dark:text-subtle-dark">No per-system breakdown.</p>
  }
  const max = Math.max(...top.map((t) => t.value), 1)
  return (
    <div className="space-y-3">
      {top.slice(0, 5).map((t) => (
        <div key={t.name}>
          <div className="flex justify-between text-xs mb-1">
            <span className="text-text-light dark:text-text-dark font-medium truncate max-w-[60%]">
              {t.name}
            </span>
            <MoneyValue
              value={t.value}
              currency={currency}
              className="text-subtle-light dark:text-subtle-dark"
            />
          </div>
          <div className="h-2 rounded-full bg-container-light dark:bg-container-dark overflow-hidden">
            <div
              className="h-full rounded-full bg-primary dark:bg-secondary"
              style={{ width: `${(t.value / max) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}
